import React, { useState } from 'react'
import { NavLink as RouterLink } from 'react-router-dom'
import { Box, Button, Collapse, ListItem } from '@mui/material'

import ChevronDownIcon from '../../../icons/ChevronDown'
import ChevronRightIcon from '../../../icons/ChevronRight'
import { isPathExternal } from '../../../utils/helpers'

const iconColorSx = { '.MuiButton-startIcon': { color: (theme: any) => theme.sidebar.iconColor } }

interface NavItemProps {
  active?: boolean
  children?: React.ReactNode
  depth?: number
  icon?: React.ReactNode
  info?: React.ReactNode
  open?: boolean
  path?: string
  title: string
  collapsed?: boolean
  childItems?: NavItemProps[]
  style?: React.CSSProperties
}

const NavItem = ({
  active = false,
  childItems,
  depth = 0,
  icon,
  info,
  open: openProp = false,
  path,
  title,
  collapsed,
  ...other
}: NavItemProps) => {
  const [open, setOpen] = useState(openProp)

  const handleToggle = () => {
    setOpen(prevOpen => !prevOpen)
  }

  let paddingLeft = 16
  if (depth > 0) {
    paddingLeft = 32 + 8 * depth
  }

  // Branch
  if (childItems && childItems.length > 0) {
    return (
      <ListItem
        disableGutters
        title={title}
        sx={{
          display: 'block',
          py: 0,
        }}
        {...other}
      >
        <Button
          endIcon={!collapsed && (!open ? <ChevronRightIcon fontSize="small" /> : <ChevronDownIcon fontSize="small" />)}
          onClick={handleToggle}
          sx={{
            color: 'text.secondary',
            fontWeight: 'fontWeightMedium',
            justifyContent: collapsed ? 'center' : 'flex-start',
            px: collapsed ? 0 : `${paddingLeft}px`,
            py: '8px',
            textAlign: 'left',
            textTransform: 'none',
            width: collapsed ? '40px' : '100%',
            height: '40px',
            minWidth: 'unset',
          }}
          variant="text"
        >
          <span
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '1.3rem',
              width: '1.3rem',
              flexShrink: 0,
              marginRight: collapsed ? 0 : '12px',
            }}
          >
            {icon}
          </span>
          {!collapsed && <Box sx={{ flexGrow: 1, fontWeight: 'bold', color: theme => theme.typography.textColor.primary }}>{title}</Box>}
          {!collapsed && info}
        </Button>
        <Collapse in={open}>
          {childItems.map(child => (
            <NavItem key={child.path ?? child.title} {...child} depth={depth + 1} collapsed={collapsed} />
          ))}
        </Collapse>
      </ListItem>
    )
  }

  const styles = {
    color: (theme: any) => theme.typography.textColor.primary,
    fontWeight: 'fontWeightMedium',
    display: 'flex !important',
    justifyContent: collapsed ? 'center' : 'flex-start',
    alignItems: 'center',
    textAlign: 'left',
    px: collapsed ? 0 : '10px',
    py: '8px',
    width: collapsed ? '40px' : '100%',
    height: '40px',
    minWidth: 'unset',
    textTransform: 'none',
    whiteSpace: 'nowrap',
    '&:hover': {
      color: (theme: any) => theme.typography.textColor.richBlack,
      backgroundColor: 'hover.main',
    },
    ...(active && {
      color: (theme: any) => theme.typography.textColor.richBlack,
      backgroundColor: 'primary.main',
      fontWeight: 'fontWeightBold',
      '& svg': {
        color: 'primary.contrastText',
      },
      '&:hover': {
        backgroundColor: (theme: any) => theme.typography.button.hover,
      },
    }),
  }

  // Leaf
  return (
    <ListItem
      disableGutters
      title={title}
      sx={{
        display: 'flex',
        justifyContent: collapsed ? 'center' : 'flex-start',
        my: '4px',
        py: 0,
      }}
    >
      {isPathExternal(path) ? (
        <Button component="a" href={path} target="_blank" rel="noopener noreferrer" sx={[iconColorSx, styles]} variant="text" {...other}>
          <span
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '1.3rem',
              width: '1.3rem',
              flexShrink: 0,
              marginRight: collapsed ? 0 : '12px',
            }}
          >
            {icon}
          </span>
          {!collapsed && title}
          {info}
        </Button>
      ) : (
        <Button component={path ? RouterLink : 'button'} to={path} sx={[iconColorSx, styles]} variant="text" {...other}>
          <span
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '1.3rem',
              width: '1.3rem',
              flexShrink: 0,
              marginRight: collapsed ? 0 : '12px',
            }}
          >
            {icon}
          </span>
          {!collapsed && title}
          {info}
        </Button>
      )}
    </ListItem>
  )
}

export default NavItem
